'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import {
  Smartphone,
  ArrowRight,
  ShieldBan,
  Fingerprint,
  Lock,
  ScanSearch,
  Radio,
  KeyRound,
  Bug,
  FileText,
  Zap,
  ServerCog,
  LayoutDashboard,
  CheckCircle2,
  XCircle,
  Boxes,
  Upload,
  Cpu,
  ClipboardList,
  Wifi,
  Database,
  Braces,
  UserCheck,
  Globe2,
  Layers,
  Apple,
  Bot,
} from 'lucide-react';
import Link from 'next/link';
import { platformLink } from '@/lib/config';

const BLOCKING = [
  {
    icon: ShieldBan,
    label: 'Auto-block API abuse',
    desc: 'Malicious requests from mobile clients are blocked with a 403 in real time - SQLi, XSS, IDOR, prompt injection.',
  },
  {
    icon: Radio,
    label: 'Rate limiting & bans',
    desc: 'Brute force, credential stuffing and scraping trigger automatic per-IP rate limits and bans.',
  },
  {
    icon: Fingerprint,
    label: 'Tamper & bot signals',
    desc: 'Surfaces jailbreak/root, repackaging and scripted traffic hitting your mobile backend.',
  },
  {
    icon: Lock,
    label: 'Data leak prevention',
    desc: 'PII and secrets in responses are redacted or blocked before they leave your API.',
  },
];

const METHOD_STEPS = [
  {
    icon: Upload,
    n: '01',
    title: 'Point Silker at your app',
    desc: 'Give us the bundle ID / package name, platform and store listing. No source code and no binary upload required to start.',
  },
  {
    icon: Boxes,
    n: '02',
    title: 'Metadata & package intake',
    desc: 'We collect the app manifest, permissions, entitlements, network config and third-party SDK footprint for the target.',
  },
  {
    icon: Cpu,
    n: '03',
    title: 'Static analysis engine',
    desc: 'Heuristics inspect storage, cryptography, transport security, binary protections and exposed components against known weak patterns.',
  },
  {
    icon: ScanSearch,
    n: '04',
    title: 'OWASP Mobile Top 10 mapping',
    desc: 'Every signal is mapped to an OWASP Mobile Top 10 (2024) category and scored by severity and exploitability.',
  },
  {
    icon: ClipboardList,
    n: '05',
    title: 'Prioritised report',
    desc: 'You get a security grade, a 0-100 score, and per-finding remediation your mobile team can act on immediately.',
  },
];

const OWASP = [
  { code: 'M1', icon: KeyRound, title: 'Improper Credential Usage', desc: 'Hardcoded keys, tokens and credentials embedded in the binary.' },
  { code: 'M2', icon: Boxes, title: 'Inadequate Supply Chain Security', desc: 'Outdated or vulnerable third-party SDKs and dependencies.' },
  { code: 'M3', icon: UserCheck, title: 'Insecure Authentication', desc: 'Weak token handling, long-lived sessions, no revocation.' },
  { code: 'M4', icon: Braces, title: 'Insufficient Input/Output Validation', desc: 'Unsafe WebView bridges and unvalidated deep links.' },
  { code: 'M5', icon: Wifi, title: 'Insecure Communication', desc: 'Cleartext traffic, weak ATS and missing certificate pinning.' },
  { code: 'M6', icon: Globe2, title: 'Inadequate Privacy Controls', desc: 'Excessive permissions and leakage of sensitive data.' },
  { code: 'M7', icon: Bug, title: 'Insufficient Binary Protections', desc: 'Debuggable builds, no anti-tampering, no root/jailbreak checks.' },
  { code: 'M8', icon: ServerCog, title: 'Security Misconfiguration', desc: 'Exported components and permissive backup settings.' },
  { code: 'M9', icon: Database, title: 'Insecure Data Storage', desc: 'Plaintext secrets in Keychain, Keystore, prefs and SQLite.' },
  { code: 'M10', icon: Fingerprint, title: 'Insufficient Cryptography', desc: 'MD5/SHA-1, ECB mode and predictable key handling.' },
];

const REPORT_FEATURES = [
  { icon: ShieldBan, title: 'Security grade & score', desc: 'An at-a-glance A-F grade and 0-100 score for every scan.' },
  { icon: Layers, title: 'Severity breakdown', desc: 'Findings grouped by critical / high / medium / low with counts.' },
  { icon: ClipboardList, title: 'Remediation guidance', desc: 'Each finding ships with a concrete fix your devs can apply.' },
  { icon: FileText, title: 'Shareable history', desc: 'Every scan is stored so you can track progress release over release.' },
];

const COMPARISON = [
  { label: 'Time to first result', silker: 'Minutes', manual: 'Days to weeks', none: '-' },
  { label: 'OWASP Mobile Top 10 coverage', silker: true, manual: true, none: false },
  { label: 'Repeatable on every release', silker: true, manual: false, none: false },
  { label: 'Runtime blocking of API abuse', silker: true, manual: false, none: false },
  { label: 'Cost per scan', silker: 'Included in plan', manual: '$$$', none: 'Free (unprotected)' },
  { label: 'Prioritised remediation report', silker: true, manual: true, none: false },
];

const FAQ = [
  {
    q: 'Do I need to upload my APK or IPA?',
    a: 'No. The automated assessment runs from your app identifier and store listing - you can start a scan in seconds without shipping us a binary. Deeper binary analysis is on our roadmap for teams that want it.',
  },
  {
    q: 'How is this different from a WAF?',
    a: 'A WAF filters traffic at the edge. Silker does two things a WAF cannot: it blocks attacks inside the API your app talks to (runtime protection), and it assesses the mobile app itself for client-side weaknesses like insecure storage and weak crypto.',
  },
  {
    q: 'Which platforms are supported?',
    a: 'Both iOS and Android. The assessment adapts its checks per platform - for example App Transport Security on iOS, and exported components and allowBackup on Android.',
  },
  {
    q: 'Does the runtime protection require an app store release?',
    a: 'No. Runtime blocking runs on the backend your app calls, so you can turn it on today with zero changes shipped to the app store.',
  },
];

/** Polished iPhone 15 Pro-style mockup showing an example Silker mobile pentest report. */
function PhoneMockup() {
  const findings = [
    { sev: 'HIGH', label: 'Sensitive data stored unencrypted', cat: 'M9', icon: Database, tone: 'text-rose-300 bg-rose-500/15 border-rose-500/40' },
    { sev: 'HIGH', label: 'Hardcoded secrets in binary', cat: 'M1', icon: KeyRound, tone: 'text-rose-300 bg-rose-500/15 border-rose-500/40' },
    { sev: 'MED', label: 'No TLS certificate pinning', cat: 'M5', icon: Wifi, tone: 'text-amber-300 bg-amber-500/15 border-amber-500/40' },
    { sev: 'MED', label: 'No root / jailbreak detection', cat: 'M7', icon: Bug, tone: 'text-amber-300 bg-amber-500/15 border-amber-500/40' },
    { sev: 'LOW', label: 'App backup enabled', cat: 'M8', icon: ServerCog, tone: 'text-blue-300 bg-blue-500/15 border-blue-500/40' },
  ];

  // Score ring geometry (78 / 100)
  const R = 30;
  const C = 2 * Math.PI * R;
  const score = 78;
  const dash = (score / 100) * C;

  return (
    <div className="relative mx-auto w-[300px]">
      {/* ambient glow */}
      <div className="absolute -inset-10 bg-[radial-gradient(ellipse_at_center,rgba(244,63,94,0.22),transparent_65%)] blur-3xl" />

      {/* titanium frame */}
      <div className="relative rounded-[3.4rem] bg-gradient-to-b from-neutral-700 via-neutral-800 to-neutral-900 p-[3px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)]">
        {/* side buttons */}
        <div className="absolute -left-[3px] top-[120px] h-8 w-[3px] rounded-l bg-neutral-600" />
        <div className="absolute -left-[3px] top-[168px] h-12 w-[3px] rounded-l bg-neutral-600" />
        <div className="absolute -left-[3px] top-[228px] h-12 w-[3px] rounded-l bg-neutral-600" />
        <div className="absolute -right-[3px] top-[186px] h-16 w-[3px] rounded-r bg-neutral-600" />

        <div className="rounded-[3.2rem] bg-black p-[10px]">
          {/* screen */}
          <div className="relative overflow-hidden rounded-[2.6rem] bg-gradient-to-b from-neutral-950 via-black to-neutral-950">
            {/* subtle top wallpaper glow */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_at_top,rgba(244,63,94,0.15),transparent_70%)]" />

            {/* Dynamic Island */}
            <div className="absolute left-1/2 top-3 z-20 flex h-8 w-24 -translate-x-1/2 items-center justify-center rounded-full bg-black shadow-inner">
              <div className="h-2 w-2 rounded-full bg-neutral-800 ring-1 ring-neutral-700" />
            </div>

            {/* status bar */}
            <div className="relative z-10 flex items-center justify-between px-7 pt-4 pb-1 text-[11px] font-medium text-white">
              <span>9:41</span>
              <div className="flex items-center gap-1.5">
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" className="opacity-90">
                  <rect x="0" y="7" width="2.5" height="4" rx="0.6" fill="white" />
                  <rect x="4" y="5" width="2.5" height="6" rx="0.6" fill="white" />
                  <rect x="8" y="3" width="2.5" height="8" rx="0.6" fill="white" />
                  <rect x="12" y="1" width="2.5" height="10" rx="0.6" fill="white" />
                </svg>
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" className="opacity-90">
                  <path d="M8 2.5c2 0 3.8.8 5 2l-1 1c-1-.9-2.5-1.5-4-1.5S4 4.6 3 5.5l-1-1c1.2-1.2 3-2 5-2z" fill="white" />
                  <path d="M8 5.5c1.1 0 2.1.4 2.8 1.1l-1 1c-.5-.4-1.1-.7-1.8-.7s-1.3.3-1.8.7l-1-1C5.9 5.9 6.9 5.5 8 5.5z" fill="white" />
                  <circle cx="8" cy="9" r="1.3" fill="white" />
                </svg>
                <div className="flex items-center gap-0.5">
                  <div className="h-3 w-6 rounded-[3px] border border-white/70 p-0.5">
                    <div className="h-full w-3/4 rounded-[1px] bg-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* app content */}
            <div className="relative z-10 px-4 pb-5 pt-4">
              {/* app header */}
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-rose-700 shadow-lg shadow-rose-500/30">
                  <Smartphone className="h-4 w-4 text-white" />
                </div>
                <div className="leading-tight">
                  <div className="text-xs font-semibold text-white">Mobile Pentest</div>
                  <div className="text-[9px] text-neutral-500">Silker AI</div>
                </div>
                <span className="ml-auto rounded-full border border-rose-500/40 bg-rose-500/15 px-2 py-0.5 text-[8px] font-semibold tracking-wider text-rose-300">
                  BETA
                </span>
              </div>

              {/* score card with ring */}
              <div className="mb-3 rounded-2xl border border-white/10 bg-neutral-900/70 p-4 backdrop-blur">
                <div className="flex items-center gap-4">
                  <div className="relative h-[76px] w-[76px] shrink-0">
                    <svg viewBox="0 0 76 76" className="h-full w-full -rotate-90">
                      <circle cx="38" cy="38" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                      <circle
                        cx="38"
                        cy="38"
                        r={R}
                        fill="none"
                        stroke="url(#scoreGrad)"
                        strokeWidth="6"
                        strokeLinecap="round"
                        strokeDasharray={`${dash} ${C}`}
                      />
                      <defs>
                        <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" />
                          <stop offset="100%" stopColor="#f43f5e" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-lg font-bold leading-none text-white">{score}</span>
                      <span className="text-[8px] text-neutral-500">/ 100</span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Bot className="h-3.5 w-3.5 text-neutral-400" />
                      <span className="truncate text-sm font-semibold text-white">Acme</span>
                      <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-md border border-amber-500/40 bg-amber-500/10 text-xs font-bold text-amber-400">
                        B
                      </span>
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] text-neutral-500">com.acme.app</div>
                    <div className="mt-2 flex items-center gap-1.5 text-[9px] text-neutral-400">
                      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      Scan complete · 16 checks
                    </div>
                  </div>
                </div>
              </div>

              {/* severity counters */}
              <div className="mb-3 grid grid-cols-4 gap-1.5">
                {[
                  ['0', 'CRIT', 'text-neutral-500'],
                  ['2', 'HIGH', 'text-rose-400'],
                  ['2', 'MED', 'text-amber-400'],
                  ['1', 'LOW', 'text-blue-400'],
                ].map(([n, l, tone]) => (
                  <div key={l} className="rounded-xl border border-white/5 bg-neutral-900/50 py-2 text-center">
                    <div className={`text-base font-bold ${tone}`}>{n}</div>
                    <div className="text-[8px] uppercase tracking-wider text-neutral-500">{l}</div>
                  </div>
                ))}
              </div>

              {/* findings list */}
              <div className="mb-1 flex items-center justify-between px-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Findings</span>
                <span className="text-[9px] text-neutral-600">OWASP Mobile</span>
              </div>
              <div className="space-y-1.5">
                {findings.map(({ sev, label, cat, icon: Icon, tone }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2.5 rounded-xl border border-white/5 bg-neutral-900/50 px-2.5 py-2"
                  >
                    <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border ${tone}`}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <span className="min-w-0 flex-1 truncate text-[10px] leading-tight text-neutral-200">{label}</span>
                    <span className="shrink-0 font-mono text-[8px] text-neutral-600">{cat}</span>
                    <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[8px] font-semibold ${tone}`}>{sev}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* home indicator */}
            <div className="relative z-10 flex justify-center pb-2 pt-1">
              <div className="h-1 w-28 rounded-full bg-white/40" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Cell({ value }: { value: string | boolean }) {
  if (value === true) return <CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" />;
  if (value === false) return <XCircle className="w-4 h-4 text-neutral-600 mx-auto" />;
  return <span className="text-xs text-neutral-300">{value}</span>;
}

export default function MobilePage() {
  return (
    <div className="min-h-screen bg-black">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]" />
      </div>

      <Header />
      <ScrollAnimation />

      <main>
        {/* Hero */}
        <section className="relative pt-24 pb-16 sm:pt-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div data-scroll-animate="fade-up">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-rose-400 px-3 py-1 bg-rose-500/10 border border-rose-500/20 mb-6">
                  <Smartphone className="w-3.5 h-3.5" />
                  Mobile app security
                </span>
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-5 leading-[1.1]">
                  Automatic blocking and pentesting for mobile apps
                </h1>
                <p className="text-neutral-400 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
                  Silker protects the API behind your iOS and Android apps - blocking attacks the moment they happen -
                  and runs an automated penetration test against the app itself, aligned to the OWASP Mobile Top 10.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <Link
                    href={platformLink.register()}
                    className="inline-flex items-center justify-center h-12 px-7 bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors gap-2"
                  >
                    Run your first scan <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    href={platformLink.dashboard}
                    className="inline-flex items-center justify-center h-12 px-7 border border-white/15 text-white text-sm font-semibold hover:border-rose-500/40 transition-colors gap-2"
                  >
                    <LayoutDashboard className="w-4 h-4" /> Open dashboard
                  </Link>
                </div>

                <div className="flex flex-wrap gap-6 text-sm">
                  <div>
                    <div className="text-2xl font-bold text-white">10</div>
                    <div className="text-xs text-neutral-500">OWASP Mobile categories</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">iOS + Android</div>
                    <div className="text-xs text-neutral-500">Both platforms</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">Minutes</div>
                    <div className="text-xs text-neutral-500">To first report</div>
                  </div>
                </div>
              </div>

              <div data-scroll-animate="fade-up">
                <PhoneMockup />
              </div>
            </div>
          </div>
        </section>

        {/* Two layers */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12" data-scroll-animate="fade-up">
              <h2 className="text-3xl font-bold text-white mb-3">Two layers of mobile protection</h2>
              <p className="text-neutral-400 max-w-2xl mx-auto">
                Client-side risk lives in the app; abuse arrives through the API. Silker covers both.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4" data-scroll-animate="fade-up">
              <div className="border border-white/10 bg-neutral-900/30 p-8">
                <ServerCog className="w-6 h-6 text-rose-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Runtime protection layer</h3>
                <p className="text-sm text-neutral-400 leading-relaxed mb-4">
                  Drop the Silker runtime in front of the backend your app calls. Malicious traffic is inspected and
                  blocked in-process with roughly zero added latency, and it fails open so a Silker issue never breaks
                  your app.
                </p>
                <ul className="space-y-2">
                  {['Blocks API abuse with a 403', 'Rate limits & bans abusive IPs', 'Redacts leaking PII & secrets'].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-neutral-300">
                      <CheckCircle2 className="w-4 h-4 text-rose-400 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-white/10 bg-neutral-900/30 p-8">
                <ScanSearch className="w-6 h-6 text-rose-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">App assessment layer</h3>
                <p className="text-sm text-neutral-400 leading-relaxed mb-4">
                  Scan the app itself for insecure storage, weak crypto, missing binary protections and more, so you fix
                  client-side risk before it ships to the store.
                </p>
                <ul className="space-y-2">
                  {['OWASP Mobile Top 10 coverage', 'Security grade & 0-100 score', 'Per-finding remediation'].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-neutral-300">
                      <CheckCircle2 className="w-4 h-4 text-rose-400 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How we test */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12" data-scroll-animate="fade-up">
              <span className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-4">
                <span className="w-6 h-px bg-gradient-to-r from-transparent to-neutral-700" />
                Methodology
                <span className="w-6 h-px bg-gradient-to-l from-transparent to-neutral-700" />
              </span>
              <h2 className="text-3xl font-bold text-white mb-3">How we test your app</h2>
              <p className="text-neutral-400 max-w-2xl mx-auto">
                An automated static assessment pipeline - from intake to a prioritised report, in minutes.
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-3" data-scroll-animate="fade-up">
              {METHOD_STEPS.map(({ icon: Icon, n, title, desc }) => (
                <div key={n} className="border border-white/10 bg-neutral-900/30 p-5 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="w-5 h-5 text-rose-400" />
                    <span className="text-xs font-mono text-neutral-600">{n}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1.5">{title}</h3>
                  <p className="text-xs text-neutral-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OWASP Mobile Top 10 */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12" data-scroll-animate="fade-up">
              <h2 className="text-3xl font-bold text-white mb-3">Full OWASP Mobile Top 10 coverage</h2>
              <p className="text-neutral-400 max-w-2xl mx-auto">
                Every scan maps findings to the 2024 OWASP Mobile Top 10 - the industry standard for mobile risk.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-3" data-scroll-animate="fade-up">
              {OWASP.map(({ code, icon: Icon, title, desc }) => (
                <div key={code} className="flex gap-4 border border-white/10 bg-neutral-900/30 p-5">
                  <div className="shrink-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-none border border-rose-500/30 bg-rose-500/10 text-xs font-bold text-rose-400">
                      {code}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-neutral-400" />
                      <h3 className="text-sm font-semibold text-white">{title}</h3>
                    </div>
                    <p className="text-xs text-neutral-500 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Automatic blocking */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div data-scroll-animate="fade-up">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-rose-400" />
                  <span className="text-xs uppercase tracking-wider text-rose-400 font-semibold">Real time</span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">Automatic blocking, the moment it happens</h2>
                <p className="text-neutral-400 leading-relaxed mb-6">
                  Your mobile app already sends every request through your backend. Put Silker in front of that API and
                  malicious traffic is blocked in-process - no app store release needed.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {BLOCKING.map(({ icon: Icon, label, desc }) => (
                    <div key={label} className="border border-white/10 bg-neutral-900/30 p-4">
                      <Icon className="w-5 h-5 text-rose-400 mb-2" />
                      <div className="text-sm font-medium text-white">{label}</div>
                      <div className="text-xs text-neutral-500 mt-1 leading-relaxed">{desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* live block feed */}
              <div data-scroll-animate="fade-up">
                <div className="border border-white/10 bg-neutral-950 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-neutral-900/50">
                    <div className="flex gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                    </div>
                    <span className="text-xs text-neutral-500 font-mono ml-2">silker · live feed</span>
                  </div>
                  <div className="p-4 font-mono text-xs space-y-2">
                    {[
                      ['BLOCKED', 'SQL Injection', '/api/login', 'text-rose-400'],
                      ['BLOCKED', 'IDOR', '/api/users/2841', 'text-rose-400'],
                      ['BLOCKED', 'Rate limit', '203.0.113.44', 'text-amber-400'],
                      ['REDACTED', 'PII in response', '/api/profile', 'text-blue-400'],
                      ['BLOCKED', 'Prompt injection', '/api/assistant', 'text-rose-400'],
                    ].map(([tag, type, path, tone], i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className={`shrink-0 w-20 ${tone}`}>{tag}</span>
                        <span className="text-neutral-300 shrink-0 w-28 truncate">{type}</span>
                        <span className="text-neutral-600 truncate">{path}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12" data-scroll-animate="fade-up">
              <h2 className="text-3xl font-bold text-white mb-3">What you get in every report</h2>
              <p className="text-neutral-400 max-w-2xl mx-auto">
                Findings your team can act on - not a wall of raw output.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-scroll-animate="fade-up">
              {REPORT_FEATURES.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="border border-white/10 bg-neutral-900/30 p-6">
                  <Icon className="w-6 h-6 text-rose-400 mb-3" />
                  <h3 className="text-sm font-semibold text-white mb-1.5">{title}</h3>
                  <p className="text-xs text-neutral-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12" data-scroll-animate="fade-up">
              <h2 className="text-3xl font-bold text-white mb-3">Silker vs the alternatives</h2>
              <p className="text-neutral-400 max-w-2xl mx-auto">
                Continuous, automated coverage instead of a once-a-year manual engagement.
              </p>
            </div>
            <div className="border border-white/10 bg-neutral-900/30 overflow-x-auto" data-scroll-animate="fade-up">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-xs font-semibold text-neutral-400 uppercase tracking-wider px-5 py-4"> </th>
                    <th className="text-center text-xs font-semibold text-rose-400 uppercase tracking-wider px-5 py-4">Silker</th>
                    <th className="text-center text-xs font-semibold text-neutral-400 uppercase tracking-wider px-5 py-4">Manual pentest</th>
                    <th className="text-center text-xs font-semibold text-neutral-400 uppercase tracking-wider px-5 py-4">No testing</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {COMPARISON.map((row) => (
                    <tr key={row.label}>
                      <td className="text-sm text-neutral-300 px-5 py-4">{row.label}</td>
                      <td className="text-center px-5 py-4 bg-rose-500/[0.04]"><Cell value={row.silker} /></td>
                      <td className="text-center px-5 py-4"><Cell value={row.manual} /></td>
                      <td className="text-center px-5 py-4"><Cell value={row.none} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid sm:grid-cols-2 gap-4" data-scroll-animate="fade-up">
              <div className="border border-white/10 bg-neutral-900/30 p-8">
                <Apple className="w-7 h-7 text-white mb-4" />
                <h3 className="text-lg font-semibold text-white mb-3">iOS</h3>
                <ul className="space-y-2">
                  {['App Transport Security checks', 'Keychain storage review', 'App Attest & anti-tampering', 'Universal Links validation'].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-neutral-400">
                      <CheckCircle2 className="w-4 h-4 text-rose-400 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-white/10 bg-neutral-900/30 p-8">
                <Bot className="w-7 h-7 text-white mb-4" />
                <h3 className="text-lg font-semibold text-white mb-3">Android</h3>
                <ul className="space-y-2">
                  {['Manifest & exported components', 'Keystore & backup settings', 'Cleartext traffic config', 'Play Integrity & obfuscation'].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-neutral-400">
                      <CheckCircle2 className="w-4 h-4 text-rose-400 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-16 border-t border-white/5">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-white mb-8 text-center" data-scroll-animate="fade-up">
              Frequently asked
            </h2>
            <div className="space-y-3" data-scroll-animate="fade-up">
              {FAQ.map(({ q, a }) => (
                <div key={q} className="border border-white/10 bg-neutral-900/30 p-6">
                  <h3 className="text-sm font-semibold text-white mb-2">{q}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 border-t border-white/5">
          <div className="max-w-3xl mx-auto px-6">
            <div className="border border-white/10 bg-neutral-900/40 p-10 text-center" data-scroll-animate="fade-up">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Secure your mobile app today</h2>
              <p className="text-sm text-neutral-400 mb-8 max-w-md mx-auto">
                Turn on automatic blocking for your API and run your first mobile pentest from the dashboard - in
                minutes, not weeks.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Link
                  href={platformLink.register()}
                  className="inline-flex items-center justify-center h-12 px-7 bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold transition-colors gap-2"
                >
                  Get started free <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/docs/quickstart"
                  className="inline-flex items-center justify-center h-12 px-7 border border-white/15 text-white text-sm font-semibold hover:border-rose-500/40 transition-colors"
                >
                  Read the quickstart
                </Link>
              </div>
              <p className="mt-6 text-xs text-neutral-600">
                Runtime protection runs on your API; the mobile pentest is an automated static assessment aligned to the
                OWASP Mobile Top 10.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
